import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Plus, Pencil, Trash2, Search, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BLANK = {
  title: '', tagline: '', category: '', price: 0, discountPrice: 0,
  duration: '', level: 'Beginner', rating: 4.5, reviews: 0, students: 0,
  instructor: '', thumbnail: '', badge: '', color: 'from-purple-500 to-indigo-600',
  skills: '', highlights: '', description: '', modules: '',
};

export default function AdminCourses() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState('');
  const [form, setForm] = useState(null);
  const { toast } = useToast();

  const load = () => api.courses().then(setRows).catch(() => {});
  useEffect(() => { load(); api.categories().then(setCats); }, []);

  const openNew = () => setForm({ ...BLANK });
  const openEdit = (r) => setForm({
    ...r,
    skills: (r.skills || []).join(', '),
    highlights: (r.highlights || []).join('\n'),
    modules: (r.modules || []).map(m => `${m.title}|${m.hours}`).join('\n'),
  });

  const save = async () => {
    const skills = form.skills.split(',').map(s => s.trim()).filter(Boolean);
    const highlights = form.highlights.split('\n').map(s => s.trim()).filter(Boolean);
    const modules = form.modules.split('\n').map(l => {
      const [t, h] = l.split('|');
      return t ? { title: t.trim(), hours: parseInt(h) || 0 } : null;
    }).filter(Boolean);
    const payload = { ...form, skills, highlights, modules, price: Number(form.price), discountPrice: Number(form.discountPrice), rating: Number(form.rating), reviews: Number(form.reviews), students: Number(form.students) };
    try {
      if (form.id) await api.admin.courseUpdate(form.id, payload);
      else await api.admin.courseCreate(payload);
      toast({ title: form.id ? 'Course updated' : 'Course created' });
      setForm(null); load();
    } catch (e) { toast({ title: 'Save failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this course?')) return;
    await api.admin.courseDelete(id);
    toast({ title: 'Deleted' });
    load();
  };

  const filtered = rows.filter(r => !q || r.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold">Courses</h1>
          <p className="text-gray-500 text-sm mt-1">{rows.length} total courses</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#6b47dc]" placeholder="Search" />
          </div>
          <Button onClick={openNew} className="bg-[#6b47dc] hover:bg-[#5a3ac0]"><Plus size={16} className="mr-1" /> Add course</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Course</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Price</th>
                <th className="px-4 py-3 font-semibold">Rating</th>
                <th className="px-4 py-3 font-semibold">Level</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={r.thumbnail} alt="" className="w-12 h-9 object-cover rounded" />
                      <div>
                        <div className="font-bold">{r.title}</div>
                        <div className="text-xs text-gray-500">{r.tagline}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize">{r.category?.replace('-', ' ')}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold">${Number(r.discountPrice).toLocaleString()}</div>
                    <div className="text-xs text-gray-400 line-through">${Number(r.price).toLocaleString()}</div>
                  </td>
                  <td className="px-4 py-3">{r.rating} ★</td>
                  <td className="px-4 py-3">{r.level}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button onClick={() => openEdit(r)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#6b47dc] hover:bg-purple-50"><Pencil size={14} /></button>
                    <button onClick={() => remove(r.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-gray-400">No courses found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-extrabold text-xl">{form.id ? 'Edit course' : 'New course'}</h3>
              <button onClick={() => setForm(null)}><X size={20} /></button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="text-xs font-bold text-gray-500">Title</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Tagline</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Category</label>
                <select className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="">Select</option>{cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-bold text-gray-500">Price</label><input type="number" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Discount Price</label><input type="number" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.discountPrice} onChange={(e) => setForm({ ...form, discountPrice: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Duration</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g. 6 Weeks" /></div>
              <div><label className="text-xs font-bold text-gray-500">Level</label>
                <select className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
                  <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
                </select>
              </div>
              <div><label className="text-xs font-bold text-gray-500">Instructor</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Badge</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Best Seller / Popular" /></div>
              <div><label className="text-xs font-bold text-gray-500">Rating</label><input type="number" step="0.1" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Reviews</label><input type="number" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.reviews} onChange={(e) => setForm({ ...form, reviews: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Students</label><input type="number" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.students} onChange={(e) => setForm({ ...form, students: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="text-xs font-bold text-gray-500">Thumbnail URL</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="text-xs font-bold text-gray-500">Skills (comma separated)</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="Python, ML, DL" /></div>
              <div className="md:col-span-2"><label className="text-xs font-bold text-gray-500">Highlights (one per line)</label><textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="text-xs font-bold text-gray-500">Description</label><textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="text-xs font-bold text-gray-500">Modules (one per line, format: Title|Hours)</label><textarea rows={4} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] font-mono text-xs" value={form.modules} onChange={(e) => setForm({ ...form, modules: e.target.value })} placeholder="Introduction|4&#10;Advanced|8" /></div>
            </div>
            <div className="sticky bottom-0 bg-white p-5 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
              <Button onClick={save} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">Save course</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
